import { BigNumber } from '@ethersproject/bignumber'
import { parseUnits } from '@ethersproject/units'
import { BN_ZERO, f18Mul, sqrt } from '../utils'

const TWO = parseUnits('2')
const ONE = parseUnits('1')

export class Oracle {
  constructor(
    public xDecimals: number,
    public yDecimals: number,
    public price: BigNumber,
    private bidExponents: BigNumber[],
    private bidQs: BigNumber[],
    private askExponents: BigNumber[],
    private askQs: BigNumber[]
  ) {}

  normalizeAmount(decimals: number, amount: BigNumber): BigNumber {
    if (decimals === 18) {
      return amount
    } else if (decimals > 18) {
      return amount.div(10 ** (decimals - 18))
    } else {
      return amount.mul(10 ** (18 - decimals))
    }
  }

  denormalizeAmount(decimals: number, amount: BigNumber): BigNumber {
    if (decimals === 18) {
      return amount
    } else if (decimals > 18) {
      return amount.mul(10 ** (decimals - 18))
    } else {
      return amount.div(10 ** (18 - decimals))
    }
  }

  // TRADE

  tradeX(xAfter: BigNumber, xBefore: BigNumber, yBefore: BigNumber) {
    const xAfterInt = this.normalizeAmount(this.xDecimals, xAfter)
    const xBeforeInt = this.normalizeAmount(this.xDecimals, xBefore)
    const yBeforeInt = this.normalizeAmount(this.yDecimals, yBefore)
    const yAfterInt = yBeforeInt.sub(this.integral(xAfterInt.sub(xBeforeInt)))
    return this.denormalizeAmount(this.yDecimals, yAfterInt)
  }

  tradeY(yAfter: BigNumber, xBefore: BigNumber, yBefore: BigNumber) {
    const yAfterInt = this.normalizeAmount(this.yDecimals, yAfter)
    const xBeforeInt = this.normalizeAmount(this.xDecimals, xBefore)
    const yBeforeInt = this.normalizeAmount(this.yDecimals, yBefore)
    const xAfterInt = xBeforeInt.add(this.integralInverted(yBeforeInt.sub(yAfterInt)))
    return this.denormalizeAmount(this.xDecimals, xAfterInt)
  }

  // INTEGRALS

  private integral(q: BigNumber): BigNumber {
    if (q.gt(BN_ZERO)) {
      return this.integralBid(q)
    } else if (q.lt(BN_ZERO)) {
      return this.integralAsk(q)
    } else {
      return BN_ZERO
    }
  }

  private integralBid(q: BigNumber) {
    let C = BN_ZERO
    for (let i = 1; i < this.bidExponents.length; i++) {
      const pPrevious = f18Mul(this.price, this.bidExponents[i - 1])
      const pCurrent = f18Mul(this.price, this.bidExponents[i])

      const qPrevious = this.bidQs[i - 1]
      const qCurrent = this.bidQs[i]

      if (q.lte(qCurrent)) {
        const X = pPrevious
          .add(pCurrent)
          .mul(qCurrent.sub(qPrevious))
          .add(pCurrent.sub(pPrevious).mul(q.sub(qCurrent)))
        const Y = q.sub(qPrevious)
        const Z = qCurrent.sub(qPrevious)
        const A = X.mul(Y).div(Z).div(TWO)
        return C.add(A)
      } else {
        const A = f18Mul(pPrevious.add(pCurrent), qCurrent.sub(qPrevious)).div(2)
        C = C.add(A)
      }
    }
    return C
  }

  private integralAsk(q: BigNumber) {
    let C = BN_ZERO
    for (let i = 1; i < this.askExponents.length; i++) {
      const pPrevious = f18Mul(this.price, this.askExponents[i - 1])
      const pCurrent = f18Mul(this.price, this.askExponents[i])

      const qPrevious = this.askQs[i - 1]
      const qCurrent = this.askQs[i]

      if (q.gte(qCurrent)) {
        const X = pPrevious
          .add(pCurrent)
          .mul(qCurrent.sub(qPrevious))
          .add(pCurrent.sub(pPrevious).mul(q.sub(qCurrent)))
        const Y = q.sub(qPrevious)
        const Z = qCurrent.sub(qPrevious)
        const A = X.mul(Y).div(Z).div(TWO)
        return C.add(A)
      } else {
        const A = f18Mul(pPrevious.add(pCurrent), qCurrent.sub(qPrevious)).div(2)
        C = C.add(A)
      }
    }
    return C
  }

  private integralInverted(s: BigNumber) {
    if (s.gt(BN_ZERO)) {
      return this.integralBidInverted(s)
    } else if (s.lt(BN_ZERO)) {
      return this.integralAskInverted(s)
    } else {
      return BN_ZERO
    }
  }

  private integralBidInverted(s: BigNumber) {
    const _s = s.add(1)
    let C = BN_ZERO
    for (let i = 1; i < this.bidExponents.length; i++) {
      const pPrevious = f18Mul(this.price, this.bidExponents[i - 1])
      const pCurrent = f18Mul(this.price, this.bidExponents[i])
      const qPrevious = this.bidQs[i - 1]
      const qCurrent = this.bidQs[i]
      const A = f18Mul(pPrevious.add(pCurrent), qCurrent.sub(qPrevious)).div(2)
      if (_s.lte(C.add(A))) {
        const c = C.sub(_s)
        const b = pPrevious
        const a = pCurrent.sub(b)
        const d = qCurrent.sub(qPrevious)
        const h = this.f18SolveQuadratic(a, b, c, d)
        return qPrevious.add(h)
      } else {
        C = C.add(A)
      }
    }
    return C
  }

  private integralAskInverted(s: BigNumber) {
    let C = BN_ZERO
    for (let i = 1; i < this.askExponents.length; i++) {
      const pPrevious = f18Mul(this.price, this.askExponents[i - 1])
      const pCurrent = f18Mul(this.price, this.askExponents[i])
      const qPrevious = this.askQs[i - 1]
      const qCurrent = this.askQs[i]
      const A = f18Mul(pPrevious.add(pCurrent), qCurrent.sub(qPrevious)).div(2)
      if (s.gte(C.add(A))) {
        const a = pCurrent.sub(pPrevious)
        const d = qCurrent.sub(qPrevious)
        const b = pPrevious
        const c = C.sub(s)
        const h = this.f18SolveQuadratic(a, b, c, d)
        return qPrevious.add(h).sub(1)
      } else {
        C = C.add(A)
      }
    }
    return C
  }

  private f18SolveQuadratic(A: BigNumber, B: BigNumber, C: BigNumber, D: BigNumber) {
    const inside = B.mul(B).sub(TWO.mul(A).mul(C).div(D))
    const sqroot = sqrt(inside)
    let x = sqroot.sub(B).mul(D).div(A)
    for (let i = 0; i < 16; i++) {
      const xPrev = x
      const z = A.mul(x)
      x = z
        .mul(x)
        .div(2)
        .sub(C.mul(D).mul(ONE))
        .div(z.add(B.mul(D)))
      if (x.gt(xPrev)) {
        if (x.sub(xPrev).lte(1)) {
          return x
        }
      } else {
        if (xPrev.sub(x).lte(1)) {
          return x
        }
      }
    }
    throw new Error('OVERFLOW')
  }

  // SPOT PRICE

  getSpotPrice(xCurrent: BigNumber, xBefore: BigNumber) {
    const xCurrentInt = this.normalizeAmount(this.xDecimals, xCurrent)
    const xBeforeInt = this.normalizeAmount(this.xDecimals, xBefore)
    const spotPriceInt = this.derivative(xCurrentInt.sub(xBeforeInt))
    if (spotPriceInt.lt(BN_ZERO)) {
      throw new Error('NEGATIVE_SPOT_PRICE')
    }
    return spotPriceInt
  }

  // DERIVATIVES

  private derivative(t: BigNumber) {
    if (t.gt(BN_ZERO)) {
      return this.derivativeBid(t)
    } else if (t.lt(BN_ZERO)) {
      return this.derivativeAsk(t)
    } else {
      return this.price
    }
  }

  private derivativeBid(t: BigNumber) {
    for (let i = 1; i < this.bidExponents.length; i++) {
      const pPrevious = f18Mul(this.price, this.bidExponents[i - 1])
      const pCurrent = f18Mul(this.price, this.bidExponents[i])
      const qPrevious = this.bidQs[i - 1]
      const qCurrent = this.bidQs[i]
      if (t.lte(qCurrent)) {
        return pCurrent.sub(pPrevious).mul(t.sub(qCurrent)).div(qCurrent.sub(qPrevious)).add(pCurrent)
      }
    }
    throw new Error('OVERFLOW')
  }

  private derivativeAsk(t: BigNumber) {
    for (let i = 1; i < this.askExponents.length; i++) {
      const pPrevious = f18Mul(this.price, this.askExponents[i - 1])
      const pCurrent = f18Mul(this.price, this.askExponents[i])
      const qPrevious = this.askQs[i - 1]
      const qCurrent = this.askQs[i]
      if (t.gte(qCurrent)) {
        return pCurrent.sub(pPrevious).mul(t.sub(qCurrent)).div(qCurrent.sub(qPrevious)).add(pCurrent)
      }
    }
    throw new Error('OVERFLOW')
  }
}
